// src/pages/subscription/SubscriptionPlansPage.jsx (UPDATED WITH CUSTOM PRICING & CONTACT SALES)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Zap, X, Loader, ArrowLeft, ArrowRight, RefreshCw, Mail, Phone, MessageCircle } from 'lucide-react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { useAuth } from '../../hooks/useAuth';
import { useSubscriptionStatus } from '../../hooks/useSubscriptionStatus';
import { 
  getAllPlans, 
  getMySubscription, 
  getMyUsage,
  getBankAccountDetails,
  initiateSubscriptionPayment,
  initiateUpgradePayment
} from '../../services/api/subscription';
import { 
  formatPrice, 
  isDowngrade, 
  isUpgrade,
  hasUsageExceedingLimit,
  calculateCyclePrice,
  formatBillingCycle
} from '../../utils/subscriptionUtils';
import PaymentMethodSelector from './components/PaymentMethodSelector';
import BankTransferPayment from './components/BankTransferPayment';
import CardPayment from './components/CardPayment';
import styles from './SubscriptionPlansPage.module.css';

const SubscriptionPlansPage = () => {
  const { t } = useLocalization();
  const { success, error } = useAlert();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isExpired, isCancelled, shouldShowWarning } = useSubscriptionStatus();

  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('monthly');
  const [isProcessing, setIsProcessing] = useState(false);

  // Contact Sales Modal
  const [showContactSalesModal, setShowContactSalesModal] = useState(false);
  const [contactSalesPlan, setContactSalesPlan] = useState(null);

  // Check if this is a renewal scenario
  const isRenewing = isExpired || isCancelled || shouldShowWarning;

  // Multi-step payment flow state
  const [modalStep, setModalStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('card_payment');
  const [bankDetails, setBankDetails] = useState(null);
  const [paymentParams, setPaymentParams] = useState(null);
  const [isLoadingBankDetails, setIsLoadingBankDetails] = useState(false);
  const [isInitiatingPayment, setIsInitiatingPayment] = useState(false);

  const currency = user?.business?.currency || 'LKR';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [plansResponse, subscriptionResponse, usageResponse] = await Promise.all([
        getAllPlans(),
        getMySubscription().catch(() => ({ subscription: null })),
        getMyUsage().catch(() => ({ usage: null }))
      ]);

      setPlans(plansResponse.plans || []);
      setCurrentSubscription(subscriptionResponse.subscription);
      setUsage(usageResponse.usage);

      // Pre-select plan and billing cycle if renewing
      if (isRenewing && subscriptionResponse.subscription) {
        const currentPlanId = subscriptionResponse.subscription.subscriptionPlanId;
        const currentPlan = (plansResponse.plans || []).find(p => p.id === currentPlanId);
        
        if (currentPlan) {
          // Pre-fill for renewal
          setSelectedBillingCycle(subscriptionResponse.subscription.billingCycle || 'monthly');
        }
      }
    } catch (err) {
      console.error('Error loading subscription data:', err);
      error(t('subscription.loadPlansFailed'), {
        title: t('common.error')
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlanClick = (plan) => {
    // Check if this is a Contact Sales plan
    if (plan.requiresContactSales) {
      setContactSalesPlan(plan);
      setShowContactSalesModal(true);
      return;
    }

    if (currentSubscription && plan.id === currentSubscription.subscriptionPlanId && !isRenewing) {
      return; // Already on this plan (and not renewing)
    }

    setSelectedPlan(plan);
    
    // Pre-select billing cycle for renewals
    if (isRenewing && currentSubscription) {
      setSelectedBillingCycle(currentSubscription.billingCycle || 'monthly');
    } else {
      setSelectedBillingCycle('monthly');
    }
    
    setModalStep(1);
    setPaymentMethod('card_payment');
    setPaymentParams(null);
    setBankDetails(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    if (isProcessing || isInitiatingPayment) {
      return;
    }
    setShowModal(false);
    setSelectedPlan(null);
    setModalStep(1);
    setPaymentMethod('card_payment');
    setPaymentParams(null);
    setBankDetails(null);
  };

  const handleCloseContactSalesModal = () => {
    setShowContactSalesModal(false);
    setContactSalesPlan(null);
  };

  const handleNextStep = async () => {
    if (modalStep === 1) {
      setModalStep(2);
    } else if (modalStep === 2) {
      await loadPaymentDetails();
      setModalStep(3);
    }
  };

  const handlePreviousStep = () => {
    if (modalStep > 1) {
      setModalStep(modalStep - 1);
    }
  };

  const loadPaymentDetails = async () => {
    try {
      if (paymentMethod === 'bank_transfer') {
        setIsLoadingBankDetails(true);
        const response = await getBankAccountDetails();
        setBankDetails(response.bankDetails);
      } else if (paymentMethod === 'card_payment') {
        setIsInitiatingPayment(true);
        
        // Determine if this is renewal or upgrade
        const isUpgradeAction = currentSubscription && 
          !isRenewing &&
          isUpgrade(getEffectivePrice(currentSubscription), selectedPlan.price);

        const response = isUpgradeAction
          ? await initiateUpgradePayment(selectedPlan.id, 'card_payment', null)
          : await initiateSubscriptionPayment(
              selectedPlan.id, 
              selectedBillingCycle, 
              'card_payment', 
              null
            );

        let payhereParams = null;
        
        if (response.paymentParams) {
          payhereParams = response.paymentParams;
        } else if (response.payment?.payhere?.formData) {
          payhereParams = {
            ...response.payment.payhere.formData,
            sandbox: true
          };
        } else if (response.payhere?.formData) {
          payhereParams = {
            ...response.payhere.formData,
            sandbox: true
          };
        }

        if (payhereParams) {
          if (response.payment?.id) {
            payhereParams.paymentId = response.payment.id;
          }
          setPaymentParams(payhereParams);
        } else {
          throw new Error('Payment parameters not found in response');
        }
      }
    } catch (err) {
      console.error('Error loading payment details:', err);
      error(err.response?.data?.message || t('subscription.payment.paymentInitFailed'), {
        title: t('common.error')
      });
      setModalStep(2);
    } finally {
      setIsLoadingBankDetails(false);
      setIsInitiatingPayment(false);
    }
  };

  const handleBankTransferSubmit = async (file) => {
    try {
      setIsProcessing(true);

      // Determine if this is renewal or upgrade
      const isUpgradeAction = currentSubscription && 
        !isRenewing &&
        isUpgrade(getEffectivePrice(currentSubscription), selectedPlan.price);

      const response = isUpgradeAction
        ? await initiateUpgradePayment(selectedPlan.id, 'bank_transfer', file)
        : await initiateSubscriptionPayment(
            selectedPlan.id, 
            selectedBillingCycle, 
            'bank_transfer', 
            file
          );

      handleCloseModal();

      success(t('subscription.payment.paymentPendingMessage'), {
        title: t('subscription.payment.paymentPending'),
        duration: 8000
      });

      // Extract data from response
      const planName = response.payment?.metadata?.planName || selectedPlan.name;
      const amount = response.payment?.amount || selectedPlan.price;
      const orderId = response.payment?.orderId || response.payment?.transactionId;
      const proofUrl = response.payment?.paymentProofUrl;

      // Navigate to pending page with URL search parameters
      const searchParams = new URLSearchParams({
        order_id: orderId || '',
        amount: amount.toString(),
        plan_name: planName,
        currency: currency
      });

      // Add proof URL if available
      if (proofUrl) {
        searchParams.append('proof_url', proofUrl);
      }

      navigate(`/subscription/payment/pending?${searchParams.toString()}`);

    } catch (err) {
      console.error('Error submitting bank transfer:', err);
      error(err.response?.data?.message || t('subscription.subscribeFailed'), {
        title: t('common.error')
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCardPaymentSuccess = async (orderId) => {
    handleCloseModal();
    await loadData();

    // Navigate to success page with URL search parameters
    const searchParams = new URLSearchParams({
      order_id: orderId || '',
      amount: selectedPlan.price.toString(),
      plan_name: selectedPlan.name,
      currency: currency
    });

    navigate(`/subscription/payment/success?${searchParams.toString()}`);
  };

  const handleCardPaymentCancel = () => {
    error(t('subscription.payment.paymentDismissed'), {
      title: t('common.warning')
    });
  };

  const handleCardPaymentError = (err) => {
    console.error('Card payment error:', err);
    error(t('subscription.payment.paymentFailedMessage'), {
      title: t('subscription.payment.paymentFailed')
    });
  };

  /**
   * Get effective price from subscription (custom price or plan price)
   */
  const getEffectivePrice = (subscription) => {
    if (!subscription) return 0;
    return subscription.effectivePrice || subscription.plan?.price || 0;
  };

  /**
   * Check if subscription has custom pricing
   */
  const hasCustomPricing = (subscription) => {
    if (!subscription) return false;
    return subscription.hasCustomPricing || 
           (subscription.customPrice !== null && subscription.customPrice !== undefined);
  };

  const isCurrentPlan = (plan) => {
    return currentSubscription && plan.id === currentSubscription.subscriptionPlanId && !isRenewing;
  };

  const canDowngrade = (plan) => {
    if (!currentSubscription || !usage) return true;
    
    const currentEffectivePrice = getEffectivePrice(currentSubscription);
    if (isDowngrade(currentEffectivePrice, plan.price)) {
      return !hasUsageExceedingLimit(usage, plan.planLimits);
    }
    
    return true;
  };

  const getActionButton = (plan) => {
    // Contact Sales plans
    if (plan.requiresContactSales) {
      return (
        <button 
          className={styles.contactSalesButton}
          onClick={() => handlePlanClick(plan)}
        >
          <Mail size={18} />
          {t('subscription.contactSales')}
        </button>
      );
    }

    // Check if this is the current plan and renewing
    const isCurrentPlanRenewing = currentSubscription && 
      plan.id === currentSubscription.subscriptionPlanId && 
      isRenewing;

    if (isCurrentPlanRenewing) {
      return (
        <button 
          className={styles.renewButton}
          onClick={() => handlePlanClick(plan)}
        >
          <RefreshCw size={18} />
          {t('subscription.renewSubscription')}
        </button>
      );
    }

    if (isCurrentPlan(plan)) {
      return (
        <button className={styles.currentPlanButton} disabled>
          <Check size={18} />
          {t('subscription.currentPlan')}
        </button>
      );
    }

    if (!canDowngrade(plan)) {
      return (
        <button className={styles.disabledButton} disabled>
          {t('subscription.downgrade')}
        </button>
      );
    }

    let buttonText = t('subscription.subscribe');
    
    if (currentSubscription && !isRenewing) {
      const currentEffectivePrice = getEffectivePrice(currentSubscription);
      buttonText = isUpgrade(currentEffectivePrice, plan.price)
        ? t('subscription.upgrade')
        : t('subscription.downgrade');
    }

    return (
      <button 
        className={styles.actionButton}
        onClick={() => handlePlanClick(plan)}
      >
        {buttonText}
      </button>
    );
  };

  const renderFeature = (feature) => {
    return (
      <div key={feature} className={styles.feature}>
        <Check className={styles.featureIcon} size={18} />
        <span>{feature}</span>
      </div>
    );
  };

  const renderPlanPrice = (plan) => {
    // Contact Sales plans
    if (plan.requiresContactSales) {
      return (
        <div className={styles.planPricing}>
          <div className={styles.customPricingBadge}>
            {t('subscription.customPricing')}
          </div>
          <div className={styles.contactForPricing}>
            {t('subscription.contactForPricing')}
          </div>
        </div>
      );
    }

    // Current plan with custom pricing
    const isCurrentWithCustom = currentSubscription && 
      plan.id === currentSubscription.subscriptionPlanId && 
      hasCustomPricing(currentSubscription);

    if (isCurrentWithCustom) {
      const effectivePrice = getEffectivePrice(currentSubscription);
      const basePrice = currentSubscription.plan?.price || plan.price;
      const hasDiscount = effectivePrice < basePrice;

      return (
        <div className={styles.planPricing}>
          {hasDiscount && (
            <div className={styles.originalPrice}>
              {formatPrice(basePrice, currency)}
            </div>
          )}
          <div className={styles.price}>
            {formatPrice(effectivePrice, currency)}
          </div>
          <div className={styles.pricePeriod}>
            {effectivePrice > 0 && (
              <>
                {t('subscription.perMonth')}
                {hasDiscount && (
                  <span className={styles.customPriceBadge}>
                    {t('subscription.customRate')}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      );
    }

    // Standard plan pricing
    return (
      <div className={styles.planPricing}>
        <div className={styles.price}>
          {formatPrice(plan.price, currency)}
        </div>
        <div className={styles.pricePeriod}>
          {plan.price > 0 && t('subscription.perMonth')}
        </div>
      </div>
    );
  };

  const renderModalContent = () => {
    if (!selectedPlan) return null;

    const planAmount = calculateCyclePrice(selectedPlan.price, selectedBillingCycle);

    if (modalStep === 1) {
      return (
        <>
          <div className={styles.modalBody}>
            <div className={styles.planInfo}>
              <h3>{selectedPlan.name}</h3>
              <p className={styles.planPrice}>
                {formatPrice(selectedPlan.price, currency)}
                {selectedPlan.price > 0 && <span>{t('subscription.perMonth')}</span>}
              </p>
            </div>

            {!currentSubscription && (
              <div className={styles.billingCycleSelector}>
                <label>{t('subscription.billingCycle')}</label>
                <div className={styles.cycleOptions}>
                  {['monthly', 'quarterly', 'yearly'].map(cycle => (
                    <button
                      key={cycle}
                      className={`${styles.cycleOption} ${selectedBillingCycle === cycle ? styles.active : ''}`}
                      onClick={() => setSelectedBillingCycle(cycle)}
                      disabled={isProcessing}
                    >
                      <div className={styles.cycleName}>{formatBillingCycle(cycle)}</div>
                      <div className={styles.cyclePrice}>
                        {formatPrice(calculateCyclePrice(selectedPlan.price, cycle), currency)}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className={styles.features}>
              <h4>{t('subscription.planFeatures')}</h4>
              {selectedPlan.features && selectedPlan.features.map(renderFeature)}
            </div>

            {currentSubscription && !isRenewing && isDowngrade(getEffectivePrice(currentSubscription), selectedPlan.price) && (
              <div className={styles.warning}>
                <p>{t('subscription.downgradeMessage', { planName: selectedPlan.name })}</p>
              </div>
            )}
          </div>

          <div className={styles.modalFooter}>
            <button 
              className={styles.cancelButton}
              onClick={handleCloseModal}
              disabled={isProcessing}
            >
              {t('common.cancel')}
            </button>
            <button 
              className={styles.confirmButton}
              onClick={handleNextStep}
              disabled={isProcessing}
            >
              {t('common.continue')}
              <ArrowRight size={18} />
            </button>
          </div>
        </>
      );
    }

    if (modalStep === 2) {
      return (
        <>
          <div className={styles.modalBody}>
            <PaymentMethodSelector
              selectedMethod={paymentMethod}
              onChange={setPaymentMethod}
              disabled={isProcessing}
            />

            <div className={styles.paymentSummaryBox}>
              <div className={styles.summaryRow}>
                <span>{t('subscription.planName')}</span>
                <span>{selectedPlan.name}</span>
              </div>
              {!currentSubscription && (
                <div className={styles.summaryRow}>
                  <span>{t('subscription.billingCycle')}</span>
                  <span>{formatBillingCycle(selectedBillingCycle)}</span>
                </div>
              )}
              <div className={`${styles.summaryRow} ${styles.totalRow}`}>
                <span>{t('subscription.payment.totalAmount')}</span>
                <span className={styles.totalAmount}>
                  {formatPrice(planAmount, currency)}
                </span>
              </div>
            </div>
          </div>

          <div className={styles.modalFooter}>
            <button 
              className={styles.backButton}
              onClick={handlePreviousStep}
              disabled={isProcessing}
            >
              <ArrowLeft size={18} />
              {t('common.back')}
            </button>
            <button 
              className={styles.confirmButton}
              onClick={handleNextStep}
              disabled={isProcessing}
            >
              {t('common.continue')}
              <ArrowRight size={18} />
            </button>
          </div>
        </>
      );
    }

    if (modalStep === 3) {
      if (isLoadingBankDetails || isInitiatingPayment) {
        return (
          <>
            <div className={styles.modalBody}>
              <div className={styles.loadingState}>
                <Loader className={styles.spinner} size={40} />
                <p>
                  {paymentMethod === 'bank_transfer'
                    ? t('subscription.payment.loadingBankDetails')
                    : t('subscription.payment.initializingPayment')}
                </p>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button 
                className={styles.backButton}
                onClick={handlePreviousStep}
              >
                <ArrowLeft size={18} />
                {t('common.back')}
              </button>
            </div>
          </>
        );
      }

      return (
        <>
          <div className={styles.modalBody}>
            {paymentMethod === 'bank_transfer' && bankDetails && (
              <BankTransferPayment
                bankDetails={bankDetails}
                amount={planAmount}
                currency={currency}
                onSubmit={handleBankTransferSubmit}
                isLoading={isProcessing}
              />
            )}

            {paymentMethod === 'card_payment' && paymentParams && (
              <CardPayment
                paymentParams={paymentParams}
                onSuccess={handleCardPaymentSuccess}
                onCancel={handleCardPaymentCancel}
                onError={handleCardPaymentError}
              />
            )}

            {!bankDetails && !paymentParams && (
              <div className={styles.errorState}>
                <p>{t('subscription.payment.paymentDetailsLoadFailed')}</p>
              </div>
            )}
          </div>

          <div className={styles.modalFooter}>
            <button 
              className={styles.backButton}
              onClick={handlePreviousStep}
              disabled={isProcessing}
            >
              <ArrowLeft size={18} />
              {t('common.back')}
            </button>
          </div>
        </>
      );
    }

    return null;
  };

  const getModalTitle = () => {
    if (!selectedPlan) return '';

    if (modalStep === 1) {
      // Check if renewing current plan
      const isRenewingCurrentPlan = currentSubscription && 
        selectedPlan.id === currentSubscription.subscriptionPlanId && 
        isRenewing;

      if (isRenewingCurrentPlan) {
        return t('subscription.confirmRenewal');
      }

      if (!currentSubscription) {
        return t('subscription.confirmSubscription');
      }
      
      const currentEffectivePrice = getEffectivePrice(currentSubscription);
      return isUpgrade(currentEffectivePrice, selectedPlan.price)
        ? t('subscription.confirmUpgrade')
        : t('subscription.confirmDowngrade');
    }

    if (modalStep === 2) {
      return t('subscription.payment.selectPaymentMethod');
    }

    if (modalStep === 3) {
      return paymentMethod === 'bank_transfer'
        ? t('subscription.payment.bankTransfer')
        : t('subscription.payment.cardPayment');
    }

    return '';
  };

  if (isLoading) {
    return (
      <div className={styles.subscriptionPlansPage}>
        <div className={styles.pageHeader}>
          <h1>{t('subscription.pageTitle')}</h1>
          <p>{t('subscription.chooseAPlan')}</p>
        </div>
        <div className={styles.loadingState}>
          <Loader className={styles.spinner} size={40} />
          <p>{t('subscription.loadingPlans')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.subscriptionPlansPage}>
      <div className={styles.pageHeader}>
        <h1>{t('subscription.pageTitle')}</h1>
        <p>{isRenewing ? t('subscription.renewYourSubscription') : t('subscription.chooseAPlan')}</p>
      </div>

      <div className={styles.plansGrid}>
        {plans.map((plan, index) => {
          const isCurrent = isCurrentPlan(plan);
          const isCurrentRenewing = currentSubscription && 
            plan.id === currentSubscription.subscriptionPlanId && 
            isRenewing;
          const isRecommended = index === 1 || index === 2;

          return (
            <div 
              key={plan.id} 
              className={`${styles.planCard} ${isCurrent ? styles.currentPlan : ''} ${isCurrentRenewing ? styles.renewingPlan : ''} ${isRecommended ? styles.recommended : ''} ${plan.requiresContactSales ? styles.contactSalesPlan : ''}`}
            >
              {isRecommended && !isCurrentRenewing && !plan.requiresContactSales && (
                <div className={styles.badge}>
                  <Zap size={14} />
                  {t('subscription.recommended')}
                </div>
              )}

              {plan.requiresContactSales && (
                <div className={styles.enterpriseBadge}>
                  {t('subscription.enterprise')}
                </div>
              )}

              {isCurrentRenewing && (
                <div className={styles.renewBadge}>
                  <RefreshCw size={14} />
                  {t('subscription.renewThisPlan')}
                </div>
              )}

              <div className={styles.planHeader}>
                <h2>{plan.name}</h2>
                <p className={styles.planDescription}>{plan.description}</p>
              </div>

              {renderPlanPrice(plan)}

              <div className={styles.planFeatures}>
                {plan.features && plan.features.map(renderFeature)}
              </div>

              <div className={styles.planAction}>
                {getActionButton(plan)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Standard Payment Modal */}
      {showModal && selectedPlan && (
        <div className={styles.modalOverlay} onClick={handleCloseModal}>
          <div 
            className={`${styles.modal} ${modalStep === 3 ? styles.wideModal : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <div className={styles.modalTitleSection}>
                <h2>{getModalTitle()}</h2>
                {modalStep > 1 && (
                  <div className={styles.stepIndicator}>
                    <span className={styles.stepText}>
                      {t('subscription.payment.step')} {modalStep} {t('subscription.payment.of')} 3
                    </span>
                    <div className={styles.stepDots}>
                      {[1, 2, 3].map(step => (
                        <div 
                          key={step}
                          className={`${styles.stepDot} ${step <= modalStep ? styles.active : ''}`}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <button 
                className={styles.closeButton}
                onClick={handleCloseModal}
                disabled={isProcessing || isInitiatingPayment}
              >
                <X size={20} />
              </button>
            </div>

            {renderModalContent()}
          </div>
        </div>
      )}

      {/* Contact Sales Modal */}
      {showContactSalesModal && contactSalesPlan && (
        <div className={styles.modalOverlay} onClick={handleCloseContactSalesModal}>
          <div 
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2>{t('subscription.contactSales')}</h2>
              <button 
                className={styles.closeButton}
                onClick={handleCloseContactSalesModal}
              >
                <X size={20} />
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.contactSalesContent}>
                <div className={styles.contactSalesHeader}>
                  <h3>{contactSalesPlan.name}</h3>
                  <p>{contactSalesPlan.description}</p>
                </div>

                <div className={styles.contactSalesInfo}>
                  <p className={styles.contactSalesText}>
                    {t('subscription.contactSalesMessage')}
                  </p>

                  <div className={styles.contactMethods}>
                    <div className={styles.contactMethod}>
                      <div className={styles.contactIcon}>
                        <Mail size={24} />
                      </div>
                      <div className={styles.contactDetails}>
                        <div className={styles.contactLabel}>{t('subscription.email')}</div>
                        <a href="mailto:sales@rettzy.com" className={styles.contactValue}>
                          sales@rettzy.com
                        </a>
                      </div>
                    </div>

                    <div className={styles.contactMethod}>
                      <div className={styles.contactIcon}>
                        <Phone size={24} />
                      </div>
                      <div className={styles.contactDetails}>
                        <div className={styles.contactLabel}>{t('subscription.phone')}</div>
                        <a href="tel:+94771234567" className={styles.contactValue}>
                          +94 77 123 4567
                        </a>
                      </div>
                    </div>

                    <div className={styles.contactMethod}>
                      <div className={styles.contactIcon}>
                        <MessageCircle size={24} />
                      </div>
                      <div className={styles.contactDetails}>
                        <div className={styles.contactLabel}>{t('subscription.whatsapp')}</div>
                        <a 
                          href="https://wa.me/94771234567" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className={styles.contactValue}
                        >
                          +94 77 123 4567
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {contactSalesPlan.features && contactSalesPlan.features.length > 0 && (
                  <div className={styles.planFeaturesSection}>
                    <h4>{t('subscription.includedFeatures')}</h4>
                    <div className={styles.features}>
                      {contactSalesPlan.features.map(renderFeature)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button 
                className={styles.cancelButton}
                onClick={handleCloseContactSalesModal}
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPlansPage;